/**
 * Header Component JavaScript
 * @fileoverview グローバルヘッダーのスティッキー機能
 */

/**
 * スティッキーヘッダーを管理するカスタム要素
 * @extends HTMLElement
 */
class StickyHeader extends HTMLElement {
  constructor() {
    super();
    /** @type {number} スクロールでtopが変化する最大値（px） */
    this.maxOffset = 32; // 2rem = 32px
    /** @type {number} topが最大値に達するまでのスクロール距離（px） */
    this.scrollDistance = 100;
  }

  /**
   * 要素がDOMに接続された時に呼ばれる
   */
  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });

    // 初期位置を設定
    this.updateHeaderPosition(window.pageYOffset || document.documentElement.scrollTop);
  }

  /**
   * ヘッダーの高さをCSS変数に設定
   */
  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  /**
   * 要素がDOMから切断された時に呼ばれる
   */
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  /**
   * スクロールイベントハンドラ
   */
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch?.isOpen) return;

    this.updateHeaderPosition(scrollTop);
    this.currentScrollTop = scrollTop;
  }

  /**
   * ヘッダーの位置を更新
   * @param {number} scrollTop - 現在のスクロール位置
   */
  updateHeaderPosition(scrollTop) {
    // スクロール量に応じてtopを0からmaxOffsetまで滑らかに変化
    const progress = Math.min(scrollTop / this.scrollDistance, 1);
    const topValue = progress * this.maxOffset;
    
    this.header.style.top = `${topValue}px`;
  }

  /**
   * メニューディスクロージャーを閉じる
   */
  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  /**
   * 検索モーダルを閉じる
   */
  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    this.searchModal?.close(false);
  }
}

customElements.define('sticky-header', StickyHeader);

/**
 * モバイルメニューの開閉を管理
 */
class MobileMenuToggle {
  constructor() {
    this.menuToggle = document.querySelector('.header__menu-toggle');
    this.mobileMenu = document.querySelector('#mobile-menu');
    this.body = document.body;
    
    if (this.menuToggle && this.mobileMenu) {
      this.init();
    }
  }

  init() {
    // メニュートグルボタンのクリックイベント
    this.menuToggle.addEventListener('click', () => {
      this.toggleMenu();
    });

    // メニュー内のリンクをクリックしたらメニューを閉じる
    const menuLinks = this.mobileMenu.querySelectorAll('a:not([aria-expanded])');
    menuLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.closeMenu();
      });
    });

    // Escapeキーでメニューを閉じる
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen()) {
        this.closeMenu();
      }
    });

    // オーバーレイクリックでメニューを閉じる
    this.mobileMenu.addEventListener('click', (e) => {
      if (e.target === this.mobileMenu) {
        this.closeMenu();
      }
    });
  }

  toggleMenu() {
    if (this.isMenuOpen()) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.mobileMenu.classList.add('menu-open');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    this.body.classList.add('menu-open');
    this.body.style.overflow = 'hidden';
  }

  closeMenu() {
    this.mobileMenu.classList.remove('menu-open');
    this.menuToggle.setAttribute('aria-expanded', 'false');
    this.body.classList.remove('menu-open');
    this.body.style.overflow = '';
  }

  isMenuOpen() {
    return this.mobileMenu.classList.contains('menu-open');
  }
}

// DOMの読み込み完了後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MobileMenuToggle();
  });
} else {
  new MobileMenuToggle();
}
